const TOXIC_CONTENT_CODE = "CONTENT_TOXIC_BLOCKED";
const MODERATION_UNAVAILABLE_CODE = "CONTENT_MODERATION_UNAVAILABLE";
const INTERNAL_PUBLISH_ERROR_CODE = "INTERNAL_PUBLISH_ERROR";

export const TOXIC_CONTENT_FEEDBACK =
  "This content was flagged as harmful. Please rephrase without strong, threatening, or targeted language and try again.";

const UNAVAILABLE_FEEDBACK =
  "Unable to validate content safety right now. Please come back again later.";

// Threat detection requires a directed target or a first-person actor so that
// discussion words (e.g. "abortion", "rape culture", "lynching in history") pass
// through. Bare topic words on their own are allowed.
const THREAT_VERBS = "kill|kll|murder|shoot|stab|rape|lynch|hang";
const MODERATION_PATTERNS = {
  severeProfanity: /\b(cunt|slut|whore|nigger|nigga|fag|faggot)\b/i,
  threat: new RegExp(
    `\\b(?:${THREAT_VERBS})\\s+(?:you|him|her|them|u|y'?all|those\\s+people)\\b` +
      `|\\b(?:i|we|imma|i'?m\\s+gonna|i'?ll|i\\s+will|we\\s+will|we'?ll)\\s+(?:going\\s+to\\s+)?(?:${THREAT_VERBS})\\b`,
    "i"
  ),
  targetedAttack:
    /\b(you|he|she|they|them|those people)\s+(are|r)\s+(trash|scum|filth|vermin|idiot|moron|worthless)\b/i,
  targetedProfanity:
    /\b(you|he|she|they|them|those people)\b[\w\s]{0,24}\b(fuck|fucking|shit|bitch|asshole|bastard)\b/i,
};

function normalizeText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeForModeration(value) {
  return normalizeText(value)
    .toLowerCase()
    .replace(/[!|]/g, "i")
    .replace(/[@]/g, "a")
    .replace(/[3]/g, "e")
    .replace(/[0]/g, "o")
    .replace(/[5$]/g, "s")
    .replace(/[7]/g, "t")
    .replace(/[^a-z0-9\s]/g, "");
}

function policyForField(fieldType) {
  const normalized = String(fieldType || "").trim().toLowerCase();
  if (normalized === "username") {
    return {
      fieldType: "username",
      checkTargetedAttack: false,
      checkProfanity: true,
      feedback: "This username appears inappropriate. Please choose another.",
    };
  }

  if (normalized === "profile_description") {
    return {
      fieldType: "profile_description",
      checkTargetedAttack: true,
      checkProfanity: true,
      feedback: TOXIC_CONTENT_FEEDBACK,
    };
  }

  // Conversation surfaces (topic + message). The platform invites hard
  // discussion — abortion, rape culture, hate speech as a topic, slurs in
  // direct personal-experience testimony. Mirror the backend Comprehend
  // tuning: only directed threats are blocked client-side. Severe
  // profanity, targeted attacks, and targeted profanity all pass through
  // so survivors can recount their experience without rephrasing.
  if (normalized === "topic") {
    return {
      fieldType: "topic",
      checkTargetedAttack: false,
      checkProfanity: false,
      feedback: TOXIC_CONTENT_FEEDBACK,
    };
  }

  return {
    fieldType: "message",
    checkTargetedAttack: false,
    checkProfanity: false,
    feedback: TOXIC_CONTENT_FEEDBACK,
  };
}

export function evaluateClientTextModeration({ fieldType, text } = {}) {
  const policy = policyForField(fieldType);
  const normalized = normalizeText(text);
  const normalizedForModeration = normalizeForModeration(text);
  const isMessageField = policy.fieldType === "message";

  if (!normalized) {
    return {
      blocked: false,
      reason: null,
      feedback: "",
    };
  }

  if (
    MODERATION_PATTERNS.threat.test(normalized) ||
    MODERATION_PATTERNS.threat.test(normalizedForModeration)
  ) {
    return {
      blocked: true,
      reason: "threat",
      feedback: isMessageField
        ? "This message appears to contain threatening language and cannot be sent."
        : policy.feedback,
    };
  }

  if (
    policy.checkProfanity &&
    (MODERATION_PATTERNS.severeProfanity.test(normalized) ||
      MODERATION_PATTERNS.severeProfanity.test(normalizedForModeration) ||
      MODERATION_PATTERNS.targetedProfanity.test(normalized) ||
      MODERATION_PATTERNS.targetedProfanity.test(normalizedForModeration))
  ) {
    return {
      blocked: true,
      reason: "profanity",
      feedback: isMessageField
        ? "This message appears to contain abusive language and cannot be sent."
        : policy.feedback,
    };
  }

  if (
    policy.checkTargetedAttack &&
    (MODERATION_PATTERNS.targetedAttack.test(normalized) ||
      MODERATION_PATTERNS.targetedAttack.test(normalizedForModeration))
  ) {
    return {
      blocked: true,
      reason: "targeted_attack",
      feedback: isMessageField
        ? "This message appears to contain harassment and cannot be sent."
        : policy.feedback,
    };
  }

  return {
    blocked: false,
    reason: null,
    feedback: "",
  };
}

function toErrorMessages(errorLike) {
  if (!errorLike) return [];

  const messages = [];

  if (typeof errorLike === "string") {
    messages.push(errorLike);
  }

  const directMessage = String(errorLike?.message || "").trim();
  if (directMessage) {
    messages.push(directMessage);
  }

  const nestedErrors = Array.isArray(errorLike?.errors) ? errorLike.errors : [];
  for (const entry of nestedErrors) {
    const msg = String(entry?.message || entry || "").trim();
    if (msg) {
      messages.push(msg);
    }
  }

  return [...new Set(messages)];
}

function parseModerationCategory(text) {
  const match = String(text || "").match(/category:\s*([A-Z_]+)/i);
  if (!match?.[1]) return null;
  return String(match[1]).trim().toUpperCase();
}

function toxicFeedbackForCategory(category) {
  const normalized = String(category || "").trim().toUpperCase();
  if (!normalized) return TOXIC_CONTENT_FEEDBACK;
  if (normalized.includes("THREAT")) {
    return "Message blocked: this was classified as a threat. Please remove threatening language and try again.";
  }
  if (normalized.includes("HATE")) {
    return "Message blocked: this was classified as hate speech. Please remove slurs or hateful framing and try again.";
  }
  if (normalized.includes("HARASSMENT")) {
    return "Message blocked: this was classified as harassment. Please remove targeted attacks and try again.";
  }
  if (normalized.includes("VIOLENCE")) {
    return "Message blocked: this was classified as violent content. Please rephrase without violent language and try again.";
  }
  if (normalized.includes("SEXUAL")) {
    return "Message blocked: this was classified as sexual content. Please remove explicit language and try again.";
  }
  if (normalized.includes("ABUSE")) {
    return "Message blocked: this was classified as abusive content. Please rephrase respectfully and try again.";
  }
  return `Message blocked: this was classified as ${normalized.toLowerCase().replace(/_/g, " ")}. Please edit and try again.`;
}

export function extractModerationFeedback(errorLike) {
  const messages = toErrorMessages(errorLike);
  if (!messages.length) {
    return { isModerationError: false, feedback: "" };
  }

  const joined = messages.join(" | ");

  if (joined.includes(TOXIC_CONTENT_CODE) || /considered\s+toxic/i.test(joined)) {
    const category = parseModerationCategory(joined);
    return { isModerationError: true, feedback: toxicFeedbackForCategory(category) };
  }

  if (joined.includes(MODERATION_UNAVAILABLE_CODE)) {
    return { isModerationError: true, feedback: UNAVAILABLE_FEEDBACK };
  }

  if (joined.includes(INTERNAL_PUBLISH_ERROR_CODE)) {
    return {
      isModerationError: true,
      feedback: "Internal error. Please try again later.",
    };
  }

  return { isModerationError: false, feedback: "" };
}
